import { useTranslation } from "react-i18next";
import { DownloadIcon, Trash, Eye } from "lucide-react";
import { useMemo, useState, useCallback, memo, useRef } from "react";
import {
  useGetUserImageGenerators,
  useDeleteImageGenerator,
} from "@packages/api/madlenAPI";
import { format } from "date-fns";
import { enUS, tr } from "date-fns/locale";
import { ImageGeneratorResponseDto } from "@packages/api/madlenAPI.schemas";
import CreateImage from "../../components/imagen/CreateImage";
import ImagePreviewModal from "../../components/imagen/ImagePreviewModal";
import { Text, Button } from "@core/index";
import DeleteConfirmPopUp from "@features/tools/components/tool_components/DeleteConfirmPopUp";
import { useToast } from "@packages/contexts/ToastContext";
import { useQueryClient } from "@tanstack/react-query";
import ToolHelper from "@features/tools/components/ToolHelper";
import { motion, AnimatePresence } from "framer-motion";

const ProcessingShimmer = memo(({ index }: { index: number }) => {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      className="processing-shimmer"
      style={{
        height: `${Math.random() * 200 + 250}px`,
        minHeight: "200px",
      }}
    >
      <div className="shimmer-background" />
      <div className="shimmer-wave" />
      <div className="shimmer-overlay" />

      {/* Processing indicator */}
      <div className="processing-indicator">
        <div className="processing-badge">
          <div className="processing-spinner" />
          <Text variant="span" className="text-sm font-medium">
            {t("imagen.aiGeneratingShort")}
          </Text>
        </div>
      </div>
    </motion.div>
  );
});

ProcessingShimmer.displayName = "ProcessingShimmer";

// Masonry item component - Optimized to prevent re-renders
const MasonryItem = memo(
  ({
    item,
    index,
    onImageClick,
    onDownload,
    onDelete,
  }: {
    item: ImageGeneratorResponseDto;
    index: number;
    onImageClick: (item: ImageGeneratorResponseDto) => void;
    onDownload: (item: ImageGeneratorResponseDto, imageUrl: string) => void;
    onDelete: (itemId: number) => void;
  }) => {
    const { i18n, t } = useTranslation();

    const isProcessing =
      item.status !== "READY" || !item.images || item.images.length === 0;
    const firstImage = item.images?.[0];

    // Memoized calculations
    const { imageHeight, formattedDate } = useMemo(() => {
      let height = 280;
      if (firstImage) {
        const aspectRatio =
          typeof firstImage.aspectRatio === "string"
            ? firstImage.aspectRatio
            : "1:1";
        const getHeightFromAspectRatio = (ratio: string) => {
          switch (ratio) {
            case "16:9":
              return 200;
            case "9:16":
              return 350;
            case "4:3":
              return 250;
            case "3:4":
              return 320;
            case "3:2":
              return 220;
            case "2:3":
              return 300;
            case "1:1":
            default:
              return 280;
          }
        };
        height = getHeightFromAspectRatio(aspectRatio);
      }

      const date = item.createdAt
        ? format(new Date(item.createdAt), "MMM dd", {
            locale: i18n.language === "tr" ? tr : enUS,
          })
        : "";

      return { imageHeight: height, formattedDate: date };
    }, [firstImage, item.createdAt, i18n.language]);

    // Memoized handlers to prevent recreation on every render
    const handleClick = useCallback(
      () => onImageClick(item),
      [onImageClick, item],
    );

    const handleDownload = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (firstImage?.imageUrl) {
          onDownload(item, firstImage.imageUrl);
        }
      },
      [onDownload, item, firstImage],
    );

    const handleDelete = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete(item.id!);
      },
      [onDelete, item.id],
    );

    if (isProcessing) {
      return <ProcessingShimmer index={index} />;
    }

    if (!firstImage) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4, delay: index * 0.05 }}
        className="masonry-item-container"
        style={{ height: `${imageHeight}px` }}
        onClick={handleClick}
      >
        <div className="masonry-item-content">
          <img
            src={firstImage.imageUrl || ""}
            alt={firstImage.prompt || ""}
            className="masonry-image"
            loading="lazy"
          />

          {/* Multiple images indicator */}
          {item.images && item.images.length > 1 && (
            <div className="images-count-badge">
              <div className="images-count-content">
                <Eye className="w-3 h-3 text-gray-600 dark:text-gray-300" />
                <Text
                  variant="span"
                  className="text-xs font-medium text-gray-600 dark:text-gray-300"
                >
                  {t("imagen.multipleImages", {
                    count: item.images.length - 1,
                  })}
                </Text>
              </div>
            </div>
          )}

          {/* Hover overlay and controls */}
          <div className="hover-overlay">
            <div className="gradient-overlay" />

            <div className="hover-controls">
              <div className="date-badge">
                <Text
                  variant="span"
                  className="text-xs font-medium text-gray-600 dark:text-gray-300"
                >
                  {formattedDate}
                </Text>
              </div>

              <div className="action-buttons">
                <Button
                  variant="solid"
                  size="icon"
                  rounded="full"
                  className="action-button download-button"
                  onClick={handleDownload}
                >
                  <DownloadIcon className="w-4 h-4" />
                </Button>

                <Button
                  variant="solid"
                  size="icon"
                  rounded="full"
                  className="action-button delete-button"
                  onClick={handleDelete}
                >
                  <Trash className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  },
  // Custom comparison function to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    return (
      prevProps.item.id === nextProps.item.id &&
      prevProps.item.status === nextProps.item.status &&
      prevProps.item.images?.length === nextProps.item.images?.length &&
      prevProps.index === nextProps.index
    );
  },
);

MasonryItem.displayName = "MasonryItem";

export const ImageGeneratorView = () => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] =
    useState<ImageGeneratorResponseDto | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const galleryRef = useRef<HTMLDivElement>(null);

  const scrollToGallery = useCallback(() => {
    galleryRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const {
    data: allImages,
    isLoading,
    refetch,
  } = useGetUserImageGenerators(
    {
      size: 100,
    },
    {
      query: {
        refetchInterval: (query) => {
          const hasProcessingItems = query.state.data?.data?.some((item) => {
            return (
              item.status !== "READY" ||
              !item.images ||
              item.images.length === 0
            );
          });
          return hasProcessingItems ? 3000 : false;
        },
        refetchOnWindowFocus: true,
        refetchOnMount: true,
      },
    },
  );

  const { mutateAsync: deleteImage } = useDeleteImageGenerator();

  const handleDelete = useCallback(
    async (imageId: number) => {
      try {
        await deleteImage({ id: imageId });
        queryClient.invalidateQueries({
          queryKey: ["/image-generators/me"],
        });

        addToast({
          title: t("imagen.deleted"),
          variant: "success",
          duration: 3000,
        });
      } catch {
        addToast({
          title: t("imagen.deleteError"),
          description: t("imagen.deleteErrorDescription"),
          variant: "error",
        });
      }
    },
    [deleteImage, queryClient, addToast, t],
  );

  const isGeneratedBefore = useMemo(() => {
    if (isLoading) {
      return true;
    }
    return !!allImages?.data?.some((a) => a.images?.length);
  }, [allImages, isLoading]);

  // Memoized handlers
  const handleImageClick = useCallback((image: ImageGeneratorResponseDto) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  }, []);

  const handleDownload = useCallback(
    (item: ImageGeneratorResponseDto, imageUrl: string) => {
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = `generated-image-${item.id}.png`;
      link.click();
    },
    [],
  );

  const handleDeleteClick = useCallback((itemId: number) => {
    setShowDeletePopup(itemId);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (showDeletePopup) {
      handleDelete(showDeletePopup);
    }
  }, [showDeletePopup, handleDelete]);

  return (
    <>
      <style jsx>{`
        .masonry-container {
          column-count: 1;
          column-gap: 1rem;
          column-fill: balance;
        }

        @media (min-width: 640px) {
          .masonry-container {
            column-count: 2;
            column-gap: 1.5rem;
          }
        }

        @media (min-width: 1024px) {
          .masonry-container {
            column-count: 3;
            column-gap: 1.5rem;
          }
        }

        @media (min-width: 1280px) {
          .masonry-container {
            column-count: 4;
            column-gap: 2rem;
          }
        }

        @media (min-width: 1536px) {
          .masonry-container {
            column-count: 5;
            column-gap: 2rem;
          }
        }

        .masonry-item {
          break-inside: avoid;
          margin-bottom: 1rem;
        }

        @media (min-width: 640px) {
          .masonry-item {
            margin-bottom: 1.5rem;
          }
        }

        @media (min-width: 1280px) {
          .masonry-item {
            margin-bottom: 2rem;
          }
        }

        /* Processing Shimmer Styles */
        .processing-shimmer {
          position: relative;
          border-radius: 1rem;
          overflow: hidden;
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
          margin-bottom: 1rem;
        }

        .shimmer-background {
          position: absolute;
          inset: 0;
          background: linear-gradient(to right, #e5e7eb, #d1d5db, #e5e7eb);
        }

        .dark .shimmer-background {
          background: linear-gradient(to right, #374151, #4b5563, #374151);
        }

        .shimmer-wave {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to right,
            transparent,
            rgba(255, 255, 255, 0.2),
            transparent
          );
          animation: shine-wave 2s infinite;
        }

        .shimmer-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to bottom right,
            rgba(255, 255, 255, 0.1),
            rgba(0, 0, 0, 0.05)
          );
        }

        .dark .shimmer-overlay {
          background: linear-gradient(
            to bottom right,
            rgba(255, 255, 255, 0.05),
            rgba(0, 0, 0, 0.2)
          );
        }

        .processing-indicator {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .processing-badge {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(8px);
          border-radius: 9999px;
          padding: 0.5rem 1rem;
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .dark .processing-badge {
          background: rgba(31, 41, 55, 0.9);
        }

        .processing-spinner {
          width: 1rem;
          height: 1rem;
          border: 2px solid #3b82f6;
          border-left-color: transparent;
          border-radius: 50%;
          animation: spin 2s linear infinite;
        }

        /* Masonry Item Styles */
        .masonry-item-container {
          position: relative;
          cursor: pointer;
        }

        .masonry-item-content {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 1rem;
          overflow: hidden;
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
          transition:
            box-shadow 0.3s ease,
            transform 0.3s ease;
        }

        .masonry-item-container:hover .masonry-item-content {
          box-shadow: 0 25px 25px -5px rgb(0 0 0 / 0.25);
        }

        .masonry-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .masonry-item-container:hover .masonry-image {
          transform: scale(1.05);
        }

        .images-count-badge {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
        }

        .images-count-content {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(8px);
          border-radius: 9999px;
          padding: 0.25rem 0.5rem;
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .dark .images-count-content {
          background: rgba(31, 41, 55, 0.9);
        }

        /* Hover Overlay */
        .hover-overlay {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .masonry-item-container:hover .hover-overlay {
          opacity: 1;
        }

        .gradient-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.6),
            rgba(0, 0, 0, 0.2),
            transparent
          );
        }

        .hover-controls {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          transform: translateY(1rem);
          transition: transform 0.2s ease;
        }

        .masonry-item-container:hover .hover-controls {
          transform: translateY(0);
        }

        .date-badge {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(8px);
          border-radius: 9999px;
          padding: 0.25rem 0.75rem;
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
        }

        .dark .date-badge {
          background: rgba(31, 41, 55, 0.9);
        }

        .action-buttons {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .action-button {
          background: rgba(255, 255, 255, 0.9) !important;
          backdrop-filter: blur(8px);
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
          transition: all 0.2s ease;
        }

        .dark .action-button {
          background: rgba(31, 41, 55, 0.9) !important;
        }

        .action-button:hover {
          transform: scale(1.1);
        }

        .action-button:active {
          transform: scale(0.95);
        }

        .download-button:hover {
          background: rgba(219, 234, 254, 0.9) !important;
        }

        .dark .download-button:hover {
          background: rgba(30, 58, 138, 0.9) !important;
        }

        .delete-button:hover {
          background: rgba(254, 226, 226, 0.9) !important;
        }

        .dark .delete-button:hover {
          background: rgba(127, 29, 29, 0.9) !important;
        }

        .download-button:hover svg {
          color: #2563eb;
        }

        .delete-button:hover svg {
          color: #dc2626;
        }

        .dark .delete-button:hover svg {
          color: #f87171;
        }

        /* Animations */
        @keyframes shine-wave {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>

      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-lime-100 dark:from-gray-900 dark:to-lime-900">
        <div className="container mx-auto px-4">
          <CreateImage
            setSelectedImage={(image) => {
              setSelectedImage(image);
              setIsModalOpen(true);
            }}
            onScrollToGallery={scrollToGallery}
          />

          {isModalOpen && selectedImage && (
            <ImagePreviewModal
              images={selectedImage.images!}
              setIsModalOpen={setIsModalOpen}
              prompt={selectedImage.promptHistory[0] || ""}
            />
          )}

          {/* Masonry Gallery Section */}
          {allImages?.data && allImages.data.length > 0 && (
            <motion.div
              ref={galleryRef}
              className="mt-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center justify-between mb-8">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Text
                    variant="h2"
                    className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent"
                  >
                    {t("imagen.history.title")}
                  </Text>
                  <Text variant="p" status="subtle" className="mt-2">
                    {t("imagen.imagesCount", { count: allImages.data.length })}
                  </Text>
                </motion.div>
              </div>

              {/* Masonry Grid */}
              <div className="masonry-container">
                <AnimatePresence mode="popLayout">
                  {allImages.data.map((item, index) => (
                    <div key={item.id} className="masonry-item">
                      <MasonryItem
                        item={item}
                        index={index}
                        onImageClick={handleImageClick}
                        onDownload={handleDownload}
                        onDelete={handleDeleteClick}
                      />
                    </div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {/* Empty State */}
          {allImages?.data && allImages.data.length === 0 && !isLoading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-16 text-center py-12"
            >
              <div className="max-w-md mx-auto">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900 dark:to-purple-900 rounded-full flex items-center justify-center">
                  <Eye className="w-12 h-12 text-blue-500 dark:text-blue-400" />
                </div>
                <Text variant="h3" className="text-xl font-semibold mb-2">
                  {t("imagen.noImagesYet")}
                </Text>
                <Text variant="p" status="subtle">
                  {t("imagen.getStarted")}
                </Text>
              </div>
            </motion.div>
          )}
        </div>

        {showDeletePopup && (
          <DeleteConfirmPopUp
            title={t("imagen.confirmDelete")}
            showDeletePopup={showDeletePopup}
            setShowDeletePopup={setShowDeletePopup}
            handleDelete={handleConfirmDelete}
          />
        )}
      </div>
    </>
  );
};

export default ImageGeneratorView;
